'use client';

/**
 * The avatar — generated as SVG from a small config object.
 *
 * Illustrated character art would mean dozens of image requests, an art
 * pipeline, and a licence question on every asset. Drawing the character from
 * primitives instead gives infinite combinations for zero kilobytes, themes
 * correctly in dark mode, and prints cleanly on the certificate.
 */
import React, { memo } from 'react';
import type { AvatarConfig } from '../types';

const BACKGROUNDS: Record<string, [string, string]> = {
  mint: ['#D7F5E6', '#A7E8C8'],
  sunset: ['#FFE0B8', '#FFB4A2'],
  space: ['#2B2A5E', '#5B4B96'],
  ocean: ['#BFE9F7', '#7FC7E8'],
  forest: ['#D6EFC6', '#9CD183'],
};

const PETS: Record<string, string> = {
  cat: '🐈',
  dog: '🐕',
  dragon: '🐉',
  robot: '🤖',
};

export interface AvatarProps {
  config: AvatarConfig;
  size?: number;
  /** Adds a soft ring — used for the "this is you" treatment. */
  ring?: boolean;
  className?: string;
  /** Gentle idle float. Off in lists and on the certificate. */
  animated?: boolean;
}

function Avatar({
  config,
  size = 72,
  ring = false,
  className = '',
  animated = false,
}: AvatarProps) {
  const [bgFrom, bgTo] = BACKGROUNDS[config.background] ?? BACKGROUNDS.mint;
  const gradientId = `k-av-${config.background}`;
  const pet = PETS[config.pet];

  return (
    <span
      className={`relative inline-flex shrink-0 ${animated ? 'k-anim-bob' : ''} ${className}`}
      style={{ width: size, height: size }}
    >
      <svg
        viewBox="0 0 100 100"
        width={size}
        height={size}
        role="img"
        aria-label="Your avatar"
        style={{
          borderRadius: '50%',
          border: ring ? '3px solid var(--accent)' : '3px solid var(--border-strong)',
          background: 'var(--card)',
        }}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={bgFrom} />
            <stop offset="100%" stopColor={bgTo} />
          </linearGradient>
        </defs>

        <rect width="100" height="100" fill={`url(#${gradientId})`} />
        {config.background === 'space' && (
          <g fill="#fff" opacity="0.85">
            <circle cx="18" cy="20" r="1.6" />
            <circle cx="76" cy="14" r="1.2" />
            <circle cx="86" cy="38" r="1.8" />
            <circle cx="30" cy="42" r="1" />
          </g>
        )}

        {/* shoulders */}
        <path d="M18 100 Q50 66 82 100 Z" fill={config.hairColor} opacity="0.85" />

        {/* head */}
        <ellipse
          cx="50"
          cy="52"
          rx={config.body === 'tall' ? 24 : 27}
          ry={config.body === 'tall' ? 30 : 27}
          fill={config.skin}
          stroke="rgba(0,0,0,0.12)"
          strokeWidth="1.5"
        />

        {/* hair */}
        {config.hair === 'short' && (
          <path
            d="M23 48 Q26 22 50 22 Q74 22 77 48 Q70 34 50 34 Q30 34 23 48 Z"
            fill={config.hairColor}
          />
        )}
        {config.hair === 'long' && (
          <>
            <path
              d="M23 48 Q26 22 50 22 Q74 22 77 48 Q70 34 50 34 Q30 34 23 48 Z"
              fill={config.hairColor}
            />
            <path d="M22 46 Q16 74 24 88 Q26 66 28 54 Z" fill={config.hairColor} />
            <path d="M78 46 Q84 74 76 88 Q74 66 72 54 Z" fill={config.hairColor} />
          </>
        )}
        {config.hair === 'curly' && (
          <g fill={config.hairColor}>
            <circle cx="32" cy="32" r="11" />
            <circle cx="50" cy="26" r="12" />
            <circle cx="68" cy="32" r="11" />
            <circle cx="24" cy="44" r="8" />
            <circle cx="76" cy="44" r="8" />
          </g>
        )}
        {config.hair === 'buns' && (
          <g fill={config.hairColor}>
            <path d="M23 48 Q26 22 50 22 Q74 22 77 48 Q70 34 50 34 Q30 34 23 48 Z" />
            <circle cx="20" cy="30" r="9" />
            <circle cx="80" cy="30" r="9" />
          </g>
        )}
        {config.hair === 'spiky' && (
          <path
            d="M24 46 L30 24 L36 40 L44 18 L52 40 L60 20 L66 40 L74 26 L78 48 Q60 34 50 34 Q38 34 24 46 Z"
            fill={config.hairColor}
          />
        )}

        {/* eyes */}
        {config.eyes === 'happy' ? (
          <g stroke="#20201C" strokeWidth="3" strokeLinecap="round" fill="none">
            <path d="M38 52 Q42 47 46 52" />
            <path d="M54 52 Q58 47 62 52" />
          </g>
        ) : config.eyes === 'wink' ? (
          <g stroke="#20201C" strokeWidth="3" strokeLinecap="round" fill="none">
            <path d="M38 51 Q42 47 46 51" />
            <circle cx="58" cy="51" r="3.4" fill="#20201C" stroke="none" />
          </g>
        ) : (
          <g fill="#20201C">
            <circle cx="42" cy="51" r="3.6" />
            <circle cx="58" cy="51" r="3.6" />
          </g>
        )}

        {/* smile */}
        <path
          d="M42 62 Q50 70 58 62"
          stroke="#20201C"
          strokeWidth="2.6"
          fill="none"
          strokeLinecap="round"
        />

        {/* accessories */}
        {config.accessory === 'glasses' && (
          <g stroke="#2B2B2B" strokeWidth="2.4" fill="none">
            <circle cx="42" cy="51" r="8" />
            <circle cx="58" cy="51" r="8" />
            <line x1="50" y1="51" x2="50" y2="51" strokeLinecap="round" />
            <line x1="34" y1="49" x2="28" y2="47" />
            <line x1="66" y1="49" x2="72" y2="47" />
          </g>
        )}
        {config.accessory === 'cap' && (
          <g>
            <path d="M22 40 Q50 12 78 40 Z" fill="var(--k-melon)" />
            <rect x="18" y="38" width="64" height="7" rx="3.5" fill="var(--k-melon)" />
          </g>
        )}
        {config.accessory === 'headphones' && (
          <g fill="none" stroke="#33343D" strokeWidth="4">
            <path d="M22 52 Q22 22 50 22 Q78 22 78 52" />
            <rect x="15" y="48" width="11" height="18" rx="5" fill="#33343D" />
            <rect x="74" y="48" width="11" height="18" rx="5" fill="#33343D" />
          </g>
        )}
        {config.accessory === 'crown' && (
          <path
            d="M28 30 L34 16 L42 26 L50 12 L58 26 L66 16 L72 30 Z"
            fill="#F2C230"
            stroke="#C79A16"
            strokeWidth="1.5"
          />
        )}
      </svg>

      {pet && (
        <span
          className="absolute grid place-items-center rounded-full"
          style={{
            right: -2,
            bottom: -2,
            width: Math.max(20, size * 0.36),
            height: Math.max(20, size * 0.36),
            fontSize: Math.max(11, size * 0.2),
            background: 'var(--card)',
            border: '2px solid var(--border-strong)',
          }}
          role="img"
          aria-label={`Pet: ${config.pet}`}
        >
          {pet}
        </span>
      )}
    </span>
  );
}

export default memo(Avatar);

/** Palettes offered in the avatar editor. */
export const SKIN_TONES = [
  '#F7D9BE',
  '#F5C99B',
  '#E0AC7B',
  '#C68642',
  '#A9714B',
  '#7B4B2A',
  '#5A3520',
];

export const HAIR_COLORS = [
  '#1C1613',
  '#3A2A1D',
  '#6B4423',
  '#A8672B',
  '#D4A24C',
  '#B33A3A',
  '#4A6FA5',
  '#7B4BA8',
];

export const AVATAR_OPTIONS = {
  body: ['round', 'tall'],
  hair: ['short', 'long', 'curly', 'buns', 'spiky'],
  eyes: ['happy', 'round', 'wink'],
  accessory: ['none', 'glasses', 'cap', 'headphones', 'crown'],
  background: ['mint', 'sunset', 'space', 'ocean', 'forest'],
} as const;
